export const LANGUAGE_ISO = {
  bg: 'bg',
  cs: 'cs',
  da: 'da',
  de: 'de',
  en: 'en',
  es: 'es',
  fi: 'fi',
  fr: 'fr',
  hu: 'hu',
  it: 'it',
  nl: 'nl',
  pl: 'pl',
  pt: 'pt',
  sv: 'sv'
};

export type LanguageIso = keyof typeof LANGUAGE_ISO;
export type GetQuotation = (text: string) => string;
export interface Language {
  id: number;
  name: string;
  getQuotation: GetQuotation;
}
export interface Languages {
  [key: string]: Language;
}
export type GetLanguageIso = (locale: string) => LanguageIso;
export type GetLanguageIdByLanguageIso = (languageIso: LanguageIso) => number;

function getQuotationFactory(name: LanguageIso): GetQuotation {
  return (text: string): string => {
    const trimmedText = text.trim();

    switch (name) {
      case LANGUAGE_ISO.de:
        return `&bdquo;${trimmedText}&rdquo;`;
      case LANGUAGE_ISO.fr:
        return `&laquo;&nbsp;${trimmedText}&nbsp;&raquo;`;
      default:
        return `&ldquo;${trimmedText}&rdquo;`;
    }
  };
}

export function createLanguage(id: number, name: LanguageIso): Language {
  return {
    id,
    name,
    getQuotation: getQuotationFactory(name)
  };
}

export const LANGUAGES: Languages = {
  bg: createLanguage(143, 'bg'),
  cs: createLanguage(141, 'cs'),
  da: createLanguage(51, 'da'),
  de: createLanguage(0, 'de'),
  en: createLanguage(1, 'en'),
  es: createLanguage(48, 'es'),
  fi: createLanguage(61, 'fi'),
  fr: createLanguage(24, 'fr'),
  hu: createLanguage(142, 'hu'),
  it: createLanguage(47, 'it'),
  nl: createLanguage(14, 'nl'),
  pl: createLanguage(140, 'pl'),
  pt: createLanguage(1, 'pt'),
  sv: createLanguage(52, 'sv')
};

export const DEFAULT_LANGUAGE_ISO = 'en';

export function getLanguageIso(locale: string) {
  return locale.substring(0, 2) as LanguageIso;
}

export function getLanguageIdByLanguageIso(languageIso: string): number {
  return LANGUAGES[languageIso]?.id;
}

/**
 * Locale checker functions - similar to the old language.js pattern
 * These check if a locale matches a specific language/country combination
 */

/**
 * Check if locale is Finnish (fi or fi-FI)
 */
export function isFI(locale: string): boolean {
  return locale.toLowerCase().startsWith('fi');
}

/**
 * Check if locale is German (de or de-*)
 */
export function isDE(locale: string): boolean {
  return locale.toLowerCase().startsWith('de');
}

/**
 * Check if locale is Germany (de-DE)
 */
export function isGermany(locale: string): boolean {
  return locale.toLowerCase() === 'de-de';
}

/**
 * Check if locale is Austria (de-AT)
 */
export function isAT(locale: string): boolean {
  return locale.toLowerCase() === 'de-at';
}

/**
 * Check if locale is English (en or en-*)
 * Check if locale is base English (en only, not regional variants)
 * Used to identify pixum.com which uses 'en' locale without country code.
 * Regional English variants (en-GB, en-IE) should be checked with isGB() or isIE().
 */
export function isEN(locale: string): boolean {
  return locale.toLowerCase() === 'en';
}

/**
 * Check if locale is French (fr or fr-*)
 */
export function isFR(locale: string): boolean {
  return locale.toLowerCase().startsWith('fr');
}

/**
 * Check if locale is Italian (it or it-*)
 */
export function isIT(locale: string): boolean {
  return locale.toLowerCase().startsWith('it');
}

/**
 * Check if locale is Spanish (es or es-*)
 */
export function isES(locale: string): boolean {
  return locale.toLowerCase().startsWith('es');
}

/**
 * Check if locale is Dutch (nl or nl-*)
 */
export function isNL(locale: string): boolean {
  return locale.toLowerCase().startsWith('nl');
}

/**
 * Check if locale is Swedish (sv or sv-*)
 */
export function isSE(locale: string): boolean {
  return locale.toLowerCase().startsWith('sv');
}

/**
 * Check if locale is Danish (da or da-*)
 */
export function isDK(locale: string): boolean {
  return locale.toLowerCase().startsWith('da');
}

/**
 * Check if locale is Switzerland - matches any Swiss locale (de-CH, fr-CH, it-CH)
 */
export function isCH(locale: string): boolean {
  return locale.toLowerCase().endsWith('-ch');
}

/**
 * Check if locale is Portuguese (pt or pt-*)
 */
export function isPT(locale: string): boolean {
  return locale.toLowerCase().startsWith('pt');
}

/**
 * Check if locale is Polish (pl or pl-*)
 */
export function isPL(locale: string): boolean {
  return locale.toLowerCase().startsWith('pl');
}

/**
 * Check if locale is Czech (cs or cs-*)
 */
export function isCS(locale: string): boolean {
  return locale.toLowerCase().startsWith('cs');
}

/**
 * Check if locale is Bulgarian (bg or bg-*)
 */
export function isBG(locale: string): boolean {
  return locale.toLowerCase().startsWith('bg');
}

/**
 * Check if locale is Hungarian (hu or hu-*)
 */
export function isHU(locale: string): boolean {
  return locale.toLowerCase().startsWith('hu');
}

/**
 * Check if locale is Irish/Ireland (en-IE)
 * Note: Ireland uses English locale (en-IE), not Irish Gaelic (ga-IE)
 */
export function isIE(locale: string): boolean {
  return locale.toLowerCase() === 'en-ie';
}

/**
 * Check if locale is British/United Kingdom (en-GB)
 */
export function isGB(locale: string): boolean {
  return locale.toLowerCase() === 'en-gb';
}

/**
 * Check if locale is Belgium (nl-BE or fr-BE)
 * Belgium has two official languages: Dutch (nl-BE) and French (fr-BE)
 */
export function isBE(locale: string): boolean {
  return locale.toLowerCase().endsWith('-be');
}

/**
 * Check if locale is Flemish Belgium (nl-BE)
 * Used for BeCommerce certification badge
 */
export function isNLBE(locale: string): boolean {
  return locale.toLowerCase() === 'nl-be';
}

/**
 * Check if locale is a new market locale.
 * New market locales are excluded from hreflang alternate links in SEO metadata
 * and from RED alternatives in cloud pages.
 *
 * New market locales:
 * - pl-PL (Poland)
 * - hu-HU (Hungary)
 * - cs-CZ (Czech Republic)
 * - bg-BG (Bulgaria)
 * - fi-FI (Finland)
 * - pt-PT (Portugal)
 * - en-IE (Ireland)
 *
 * @param locale - The locale string to check
 * @returns true if the locale is a new market locale, false otherwise
 */
export function isNewMarketLocale(locale: string): boolean {
  return isPL(locale) || isHU(locale) || isCS(locale) || isBG(locale) || isFI(locale) || isPT(locale) || isIE(locale);
}
